import { useEffect, useState } from "react";

const BOTTOM_THRESHOLD_PX = 1;

/**
 * Detects whether the parent element of the referenced node currently overflows
 * (i.e. has a vertical scrollbar), and whether it is currently scrolled all the
 * way to the bottom. Re-checks on resize of the parent, on window resize, and on scroll.
 * @param {React.RefObject} ref - Ref attached to the child element whose parent should be monitored.
 * @param {Array} deps - Additional dependencies that should trigger a re-check (e.g. item counts).
 * @returns {{ hasOverflow: boolean, isAtBottom: boolean }}
 */
const useParentOverflow = (ref, deps = []) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const scrollContainer = ref.current?.parentElement;
    if (!scrollContainer) return undefined;

    const checkOverflow = () => {
      setHasOverflow(scrollContainer.scrollHeight > scrollContainer.clientHeight);
    };

    const checkIsAtBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      setIsAtBottom(
        scrollTop + clientHeight >= scrollHeight - BOTTOM_THRESHOLD_PX
      );
    };

    const checkAll = () => {
      checkOverflow();
      checkIsAtBottom();
    };

    checkAll();

    const resizeObserver = new ResizeObserver(checkAll);
    resizeObserver.observe(scrollContainer);
    window.addEventListener("resize", checkAll);
    scrollContainer.addEventListener("scroll", checkIsAtBottom);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkAll);
      scrollContainer.removeEventListener("scroll", checkIsAtBottom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);

  return { hasOverflow, isAtBottom };
};

export default useParentOverflow;

